import { useState, useEffect } from 'react'
import Section from './_Section'
import Image from '../helpers/Image'
import TitleBlock from '../helpers/TitleBlock'
import { transformPaddingToTailwind, lightOrDark } from '../../helpers/style'
import themeConfig from '../../../theme.config'
import HeroCtaBlock from '../helpers/HeroCtaBlock'
import HeroCards from '../helpers/HeroCards'

const renderHeroCards = (heroProps, componentPadding, textAlignment, parent) => (
  <HeroCards heroProps={heroProps} componentPadding={componentPadding} textAlignment={textAlignment} parent={parent} />
)
const SplitHeroBanner = ({ props, componentPadding, textAlignment }) => {
  const gridLayouts = {
    '7030Layout': 'lg:grid-cols-[7fr_3fr]',
    '6040Layout': 'lg:grid-cols-[6fr_4fr]',
    '5050Layout': 'lg:grid-cols-[5fr_5fr]',
    '4060Layout': 'lg:grid-cols-[4fr_6fr]',
    '3070Layout': 'lg:grid-cols-[3fr_7fr]',
  }

  const activeGridClass = gridLayouts[props.layout] || gridLayouts['5050Layout']

  // Image position (split variant only). Defaults to left to preserve existing layouts.
  // On lg+ we swap the grid order so the image and content panel exchange columns;
  // the rounded-corner classes are mirrored to match. On mobile the image always
  // stays on top (DOM order), so the lg:order-* utilities are intentionally lg-only.
  const imageOnRight = props.imagePosition === 'right'
  const imageRounding = imageOnRight ? 'lg:rounded-s-3xl lg:rounded-br-3xl' : 'lg:rounded-e-3xl lg:rounded-bl-3xl'
  const panelRounding = imageOnRight ? 'lg:rounded-e-3xl lg:rounded-bl-3xl' : 'lg:rounded-s-3xl lg:rounded-br-3xl'

  const panelBgColor = props.backgroundColor || '#4475a6'
  const panelTheme = lightOrDark(panelBgColor)
  const panelTextColor = panelTheme === 'dark' ? 'var(--color-light)' : 'var(--color-dark)'

  return (
    <>
      <Section
        {...props}
        type="heroBannerSplit"
        sectionTitle={false}
        desktopBackgroundImage={null}
        mobileBackgroundImage={null}
        backgroundColor="transparent"
        outerClass="px-0! max-w-none! mx-0!"
        innerClass={`w-full max-w-none! px-0! lg:px-8! mx-0! ${componentPadding.top} ${componentPadding.bottom}`}
        
      >
        <div
        data-cards={props.cardItems?.length > 0 ? 'yes' : 'no'}
        data-footnote-text={props.footnoteText ? 'yes' : 'no'}
          className={`flex flex-col lg:grid ${activeGridClass} lg:gap-2 lg:overflow-hidden lg:min-h-[420px]`}
        >
          <div className={`relative w-full shrink-0 aspect-[16/10] lg:aspect-auto lg:h-full ${imageOnRight ? 'lg:order-2' : 'lg:order-1'} ${imageRounding} overflow-hidden`}>
            {props.mobileImage?.src && (
              <img
                src={props.mobileImage.src}
                alt={props.desktopImage?.altText || props.title || ''}
                className={`absolute inset-0 w-full h-full object-cover ${props.desktopImage?.src ? 'lg:hidden' : ''}`}
                loading="lazy"
              />
            )}
            {props.desktopImage?.src && (
              <img
                src={props.desktopImage.src}
                alt={props.desktopImage?.altText || props.title || ''}
                className={`absolute inset-0 w-full h-full object-cover ${props.mobileImage?.src ? 'hidden lg:block' : ''}`}
                loading="lazy"
              />
            )}
          </div>

          <div
            className={`p-8 md:p-10 lg:p-10 xl:p-10 ${imageOnRight ? 'lg:order-1' : 'lg:order-2'} ${panelRounding} flex flex-col justify-center shrink-0`}
            data-panel-theme={panelTheme}
            style={{ backgroundColor: panelBgColor, color: panelTextColor }}
            data-bg={panelBgColor.toLowerCase().replace('#', '')}
          >
            <TitleBlock {...props} headingClass={`text-${textAlignment.align}`} />

            <HeroCtaBlock {...props} textAlignment={{ flex: 'start', align: 'left' }} panelTheme={panelTheme} parent='heroBannerSplit'/>
          </div>
        </div>
      </Section>
      {renderHeroCards(props, componentPadding, textAlignment, 'heroBannerSplit')}
    </>
  )
}

function HeroBanner(props) {
  const [hasCardItems, setHasCardItems] = useState(0)
  const [textAlignment, setTextAlignment] = useState({
    flex: 'center',
    align: 'center',
  })
  const componentPadding = transformPaddingToTailwind(props.componentPadding, true)

  // Determine the effective background theme so CTAs can be coloured correctly.
  // A background IMAGE (CSS background on the section) is always treated as dark
  // regardless of the backgroundColor value, because text/CTAs over images need
  // white for readability. Which image props become the CSS background depends on
  // the heroForegroundImages setting (see Section prop mapping below).
  const hasBackgroundImage = themeConfig.settings.heroForegroundImages
    ? !!(props.desktopBackgroundImage || props.mobileBackgroundImage)
    : !!(props.desktopImage || props.mobileImage)
  const sectionTheme = hasBackgroundImage ? 'dark' : lightOrDark(props.backgroundColor || '#ffffff')

  // The ?. checks if cardItems exists. The || [] provides a safe fallback.
  const validCardItems = (props.cardItems || []).filter((card) => {
    // Check strings to ensure they aren't just empty spaces
    const hasTitle = card.title && card.title.trim() !== ''
    const hasDescription = card.description && card.description.trim() !== ''

    // Check objects/links to ensure they aren't null
    const hasDesktopImage = card.desktopImage !== null
    const hasMobileImage = card.mobileImage !== null
    const hasCta = card.ctaLinkItem !== null

    // Keep the card if it has at least ONE of these valid properties
    return hasTitle || hasDescription || hasDesktopImage || hasMobileImage || hasCta
  })

  useEffect(() => {
    setHasCardItems(() => (validCardItems || []).length != 0)
  }, [validCardItems])

  // In heroForegroundImages mode the foreground image is desktopImage/mobileImage.
  // When neither is present the banner has no image overlay, so content must
  // always be centred regardless of the contentAlignment prop.
  const hasForegroundImage = themeConfig.settings.heroForegroundImages
    ? !!(props.desktopImage || props.mobileImage)
    : false

  useEffect(() => {
    setTextAlignment((prevState) => {
      const newState = { ...prevState }
      if (!hasForegroundImage) {
        newState.flex = 'center'
        newState.align = 'center'
        return newState
      }
      const alignment = props.contentAlignment?.toLowerCase() ?? 'center'
      if (newState.align !== props.contentAlignment?.toLowerCase()) {
        newState.flex = alignment === 'center' ? 'center' : 'start'
        newState.align = alignment === 'center' ? 'center' : 'left'
      }
      return newState
    })
  }, [])

  const layoutRatios = ['7030Layout', '6040Layout', '5050Layout', '4060Layout', '3070Layout']

  // --- SPLIT HERO BANNER IMPLEMENTATION ---
  if (layoutRatios.includes(props.layout)) {
    return <SplitHeroBanner props={props} componentPadding={componentPadding} textAlignment={textAlignment} />
  }

  return (
    <>
      <Section
        {...props}
        type="heroBanner"
        sectionTitle={false}
        mobileBackgroundImage={
          themeConfig.settings.heroForegroundImages ? props.mobileBackgroundImage : props.mobileImage
        }
        desktopBackgroundImage={
          themeConfig.settings.heroForegroundImages ? props.desktopBackgroundImage : props.desktopImage
        }
        innerClass={`relative flex justify-${textAlignment.flex} items-center min-h-[var(--banner-min-height-sm,480px)] lg:min-h-[var(--banner-min-height,620px)] ${componentPadding.top} ${hasCardItems ? componentPadding.mid : componentPadding.bottom} px-0! max-w-none! mx-0!`}
      >
        {themeConfig.settings.heroForegroundImages && (
          <div className="absolute inset-0 w-full h-full overflow-hidden z-0">
            {props.mobileImage && (
              <Image {...props.mobileImage} className={`w-full h-full object-cover z-0 ${props.desktopImage ? 'lg:hidden' : ''}`} />
            )}
            {props.desktopImage && (
              <Image 
                {...props.desktopImage} 
                // Otherwise, let the desktop image display and crop responsively.
                className={`w-full h-full object-cover z-0 ${props.mobileImage ? 'hidden lg:block' : ''}`} 
              />
            )}
          </div>
        )}

        {(props.title || props.description) && (
          <div 
            data-panel-theme={sectionTheme} 
            className={`relative z-10 px-6 lg:px-12 max-w-4xl w-full text-${textAlignment.align}`}
          >
            <TitleBlock {...props} headingClass={`text-${textAlignment.align}`} />
            <HeroCtaBlock {...props} textAlignment={textAlignment} panelTheme={sectionTheme} parent='heroBanner' />
          </div>
        )}
      </Section>
      {renderHeroCards(props, componentPadding, textAlignment, 'heroBanner')}
    </>
  )
}

export default HeroBanner
